/**
 * RevenueCat Web Billing provider.
 *
 * Wraps the RevenueCat Web SDK (`@revenuecat/purchases-js`) to provide
 * subscription tier, limits, and trial state to the web app. This replaces
 * the previous Stripe-based shooter subscription checkout, unifying
 * subscription management across iOS, Android, and Web.
 *
 * Place this provider after AuthProvider in the component tree — it needs
 * `useAuth()` for the user ID.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { Purchases } from '@revenuecat/purchases-js'

import { useAuth } from '@/features/auth/auth-context'
import {
  type ShooterTier,
  SHOOTER_TIER_LIMITS,
} from '@/features/payments/hooks/use-shooter-subscription'

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * RevenueCat Web API key — set in .env as VITE_REVENUECAT_WEB_API_KEY.
 * This is generated when you add a Web config in the RC dashboard.
 */
const RC_WEB_API_KEY = import.meta.env.VITE_REVENUECAT_WEB_API_KEY as string

/** RevenueCat entitlement IDs — must match RC dashboard */
const RC_ENTITLEMENTS = {
  casual: 'casual_access',
  competitor: 'competitor_access',
  athlete: 'athlete_access',
} as const

/** Trial duration in days */
const TRIAL_DURATION_DAYS = 90

// ---------------------------------------------------------------------------
// Extended tier type (includes 'trial' for web, matching mobile)
// ---------------------------------------------------------------------------

export type WebTier = ShooterTier | 'trial'

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface RevenueCatContextValue {
  /** Effective tier (accounts for trial, entitlements, and free fallback) */
  tier: WebTier
  /** Numeric & boolean limits for the current tier */
  limits: (typeof SHOOTER_TIER_LIMITS)[ShooterTier]
  /** Whether the subscription state is still loading */
  loading: boolean

  // Trial info
  /** Whether the 90-day trial is currently active */
  isTrialActive: boolean
  /** Days remaining in the trial (0 if expired or not applicable) */
  trialDaysRemaining: number

  // Subscription info
  /** Whether the user has an active paid subscription */
  isSubscribed: boolean

  // Actions
  /** Purchase a specific package via RevenueCat Web Billing */
  purchase: (packageId: string) => Promise<void>
  /** Get available packages from the current offering */
  getPackages: () => Promise<RCPackage[]>

  /** Whether RevenueCat is configured and ready */
  isConfigured: boolean
}

export interface RCPackage {
  id: string
  displayName: string
  priceString: string
  price: number
  currencyCode: string
  /** 'monthly' | 'annual' | 'lifetime' | 'custom' */
  billingPeriod: string
}

const RevenueCatContext = createContext<RevenueCatContextValue | null>(null)

// ---------------------------------------------------------------------------
// Trial calculation (same as mobile)
// ---------------------------------------------------------------------------

function calculateTrialState(userCreatedAt: string | undefined) {
  if (!userCreatedAt) {
    return { isActive: false, daysRemaining: 0 }
  }

  const created = new Date(userCreatedAt)
  const expiryDate = new Date(created.getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000)
  const now = new Date()
  const daysRemaining = Math.max(
    0,
    Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  )

  return {
    isActive: daysRemaining > 0,
    daysRemaining,
  }
}

// ---------------------------------------------------------------------------
// Resolve tier from RC CustomerInfo
// ---------------------------------------------------------------------------

function resolveTierFromEntitlements(
  entitlements: Record<string, { isActive: boolean }>,
): ShooterTier | null {
  // Check from highest to lowest tier
  if (entitlements[RC_ENTITLEMENTS.athlete]?.isActive) return 'athlete'
  if (entitlements[RC_ENTITLEMENTS.competitor]?.isActive) return 'competitor'
  if (entitlements[RC_ENTITLEMENTS.casual]?.isActive) return 'casual'
  return null
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function RevenueCatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [tier, setTier] = useState<WebTier>('free')
  const [loading, setLoading] = useState(true)
  const purchasesRef = useRef<Purchases | null>(null)
  const isConfigured = !!RC_WEB_API_KEY

  // Trial state
  const trialState = calculateTrialState(user?.created_at)

  // --------------------------------------------------
  // Initialise RevenueCat Web SDK when user changes
  // --------------------------------------------------
  useEffect(() => {
    if (!RC_WEB_API_KEY || !user?.id) {
      // Not configured or no user — resolve with trial/free
      if (trialState.isActive) {
        setTier('trial')
      } else {
        setTier('free')
      }
      setLoading(false)
      return
    }

    let cancelled = false

    async function init() {
      try {
        // Configure the RC Web SDK with the user's Supabase ID
        const purchases = Purchases.configure(RC_WEB_API_KEY, user!.id)
        purchasesRef.current = purchases

        // Fetch customer info to resolve tier
        const customerInfo = await purchases.getCustomerInfo()

        if (cancelled) return

        const entitlements: Record<string, { isActive: boolean }> = {}
        if (customerInfo.entitlements?.active) {
          for (const [key, ent] of Object.entries(customerInfo.entitlements.active)) {
            entitlements[key] = { isActive: !!ent }
          }
        }

        const rcTier = resolveTierFromEntitlements(entitlements)

        if (rcTier) {
          setTier(rcTier)
        } else if (trialState.isActive) {
          setTier('trial')
        } else {
          setTier('free')
        }
      } catch {
        // RC init failed — fall back to trial/free
        if (trialState.isActive) {
          setTier('trial')
        } else {
          setTier('free')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [user?.id, user?.created_at, trialState.isActive])

  // --------------------------------------------------
  // Purchase a package
  // --------------------------------------------------
  const purchase = useCallback(async (packageId: string) => {
    const purchases = purchasesRef.current
    if (!purchases) {
      throw new Error('RevenueCat is not configured')
    }

    // Get the current offering to find the package
    const offerings = await purchases.getOfferings()
    const currentOffering = offerings.current
    if (!currentOffering) {
      throw new Error('No current offering available')
    }

    const pkg = currentOffering.availablePackages.find((p) => p.identifier === packageId)
    if (!pkg) {
      throw new Error(`Package "${packageId}" not found in current offering`)
    }

    const { customerInfo } = await purchases.purchase({ rcPackage: pkg })

    // Resolve new tier from updated entitlements
    const entitlements: Record<string, { isActive: boolean }> = {}
    if (customerInfo.entitlements?.active) {
      for (const [key, ent] of Object.entries(customerInfo.entitlements.active)) {
        entitlements[key] = { isActive: !!ent }
      }
    }

    const newTier = resolveTierFromEntitlements(entitlements)
    if (newTier) {
      setTier(newTier)
    }
  }, [])

  // --------------------------------------------------
  // Get available packages
  // --------------------------------------------------
  const getPackages = useCallback(async (): Promise<RCPackage[]> => {
    const purchases = purchasesRef.current
    if (!purchases) return []

    try {
      const offerings = await purchases.getOfferings()
      const currentOffering = offerings.current
      if (!currentOffering) return []

      return currentOffering.availablePackages.map((pkg) => {
        const product = pkg.rcBillingProduct
        const price = product.currentPrice
        return {
          id: pkg.identifier,
          displayName: product.displayName,
          priceString: price.formattedPrice,
          price: price.amountMicros / 1_000_000,
          currencyCode:
            ((price as unknown as Record<string, string>).currency ??
              (price as unknown as Record<string, string>).currencyCode) ||
            'GBP',
          billingPeriod: product.normalPeriodDuration ?? 'lifetime',
        }
      })
    } catch {
      return []
    }
  }, [])

  // --------------------------------------------------
  // Derived values
  // --------------------------------------------------
  // Map 'trial' tier to 'athlete' limits (trial = full access)
  const effectiveLimitsTier: ShooterTier = tier === 'trial' ? 'athlete' : tier
  const limits = SHOOTER_TIER_LIMITS[effectiveLimitsTier]
  const isSubscribed = tier !== 'free' && tier !== 'trial'

  const value: RevenueCatContextValue = {
    tier,
    limits,
    loading,
    isTrialActive: trialState.isActive && tier === 'trial',
    trialDaysRemaining: tier === 'trial' ? trialState.daysRemaining : 0,
    isSubscribed,
    purchase,
    getPackages,
    isConfigured,
  }

  return <RevenueCatContext.Provider value={value}>{children}</RevenueCatContext.Provider>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRevenueCat(): RevenueCatContextValue {
  const ctx = useContext(RevenueCatContext)
  if (!ctx) {
    throw new Error('useRevenueCat must be used within a RevenueCatProvider')
  }
  return ctx
}
